import { useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import { SecondaryButton } from '@joor/design-system'
import isEmpty from 'lodash/isEmpty'

import {
  ListOrderExportTasksDocument,
  OrderExportTaskStatus,
  TriggerOrdersPDFExportTasksDocument,
} from '__generated__/atlas-types'

import { download } from 'utils'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { cleanGraphQLErrors } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'

interface PropsType {
  orderId: string
}

const DOWNLOAD_INVOICE_POLL_INTERVAL = 2000
const FAILED_DOWNLOAD_MSG = 'Failed to download invoice. Please try again!'
const TRIGGER_DOWNLOAD_MSG =
  'Download in progress. Your file will be downloaded shortly'

const DownloadInvoice = ({ orderId }: PropsType) => {
  const { renderInfoToast, renderErrorToast } = useRenderToasters()
  const [downloadInProgress, setDownloadInProgress] = useState(false)
  const { trackEvent } = useUserAnalytics()
  const [triggerOrderPdfExportTask, { loading: triggerExportLoading }] =
    useMutation(TriggerOrdersPDFExportTasksDocument, {
      client: atlasClient,
      variables: {
        orderIds: [orderId],
      },
      errorPolicy: 'none',
      onError: ({ graphQLErrors }) => {
        const errors = cleanGraphQLErrors(graphQLErrors)
        renderErrorToast({ description: errors[0] })
      },
    })

  const [listOrderExportTasks, { stopPolling }] = useLazyQuery(
    ListOrderExportTasksDocument,
    {
      client: atlasClient,
      pollInterval: DOWNLOAD_INVOICE_POLL_INTERVAL,
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (
          data.listOrderExportTasks?.exportTasks[0].status ===
          OrderExportTaskStatus.FAILURE
        ) {
          stopPolling()
          setDownloadInProgress(false)
          renderErrorToast({ description: FAILED_DOWNLOAD_MSG })
          return
        }
        if (data.listOrderExportTasks?.exportTasks[0].downloadUrl) {
          stopPolling()
          setDownloadInProgress(false)
          download(data.listOrderExportTasks?.exportTasks[0].downloadUrl)
        }
      },
      onError: () => {
        stopPolling()
        setDownloadInProgress(false)
        renderErrorToast({ description: FAILED_DOWNLOAD_MSG })
      },
    },
  )

  const downloadInvoiceHandler = () => {
    trackEvent('PAYMENTS - CLICK ON DOWNLOAD PDF')
    renderInfoToast({ description: TRIGGER_DOWNLOAD_MSG })
    triggerOrderPdfExportTask().then((resp) => {
      if (isEmpty(resp.data?.triggerOrdersPdfExportTasks.taskIds)) return
      setDownloadInProgress(true)
      listOrderExportTasks({
        variables: {
          ids: [resp.data!.triggerOrdersPdfExportTasks.taskIds[0]],
        },
      })
    })
  }
  return (
    <SecondaryButton
      iconName="download"
      disabled={triggerExportLoading || downloadInProgress}
      onClick={downloadInvoiceHandler}
    >
      Download Invoice
    </SecondaryButton>
  )
}

export default DownloadInvoice
